<template>
  <form
    class="admin-manager-form"
    @submit.prevent="isFormValid() && showConfirmation()"
    novalidate
  >
    <div class="admin-manager-form__form">
      <template v-if="provideRoleById">
        <input-field
          class="app__form-field"
          :label="'admin-manager-form.lbl-id' | globalize"
          v-model="form.id"
          :disabled="formMixin.isDisabled"
          @blur="touchField('form.id')"
          :error-message="getFieldErrorMessage('form.id')"
        />
      </template>
      <template v-else>
        <input-field
          class="app__form-field"
          :label="'admin-manager-form.lbl-email' | globalize"
          v-model="form.email"
          :disabled="formMixin.isDisabled"
          @blur="touchField('form.email')"
          :error-message="getFieldErrorMessage('form.email')"
        />
      </template>

      <button
        v-ripple
        class="admin-manager-form__btn app__button-raised"
        @click.self.prevent="provideRoleById = !provideRoleById"
      >
        <template v-if="provideRoleById">
          {{ 'admin-manager-form.lbl-by-email' | globalize }}
        </template>
        <template v-else>
          {{ 'admin-manager-form.lbl-by-id' | globalize }}
        </template>
      </button>
    </div>

    <div class="app__form-actions">
      <form-confirmation
        v-if="formMixin.isConfirmationShown"
        :is-pending="isPending"
        @ok="submit"
        @cancel="hideConfirmation"
      />
      <template v-else>
        <button
          class="app__form-submit-btn app__button-raised"
          type="submit"
          :disabled="formMixin.isDisabled"
        >
          {{ 'admin-manager-form.btn-add' | globalize }}
        </button>
      </template>
    </div>
  </form>
</template>

<script>
import FormMixin from '@/vue/mixins/form.mixin'
import { required, validateEmail } from '@validators'

import { api } from '@/api'
import { ErrorHandler } from '@/js/helpers/error-handler'
import { clearObject } from '@/js/helpers/clear-object'
import { UserRecord } from '@/js/records/entities/user.record'
import { errors } from '@/js/errors'
import { Bus } from '@/js/helpers/event-bus'
import { vueRoutes } from '@/vue-router/routes'

const REQUEST_TYPES = {
  changeRole: 'change_user_role',
}

// Hardcode Admin Role
const ROLE_ADMIN = 'admin'

export default {
  name: 'admin-manager-form',
  mixins: [ FormMixin ],

  data () {
    return {
      form: {
        email: '',
        id: '',
      },
      isFailed: false,
      isPending: false,
      provideRoleById: false,
      adminDetails: {},
      errorMessage: '',
    }
  },

  validations () {
    let form = {}

    if (this.provideRoleById) {
      form.id = { required }
    } else {
      form.email = {
        required,
        email: validateEmail,
      }
    }

    return { form }
  },

  methods: {
    async submit () {
      this.isPending = true

      try {
        let body = JSON.stringify({
          data: {
            type: REQUEST_TYPES.changeRole,
            attributes: { role: ROLE_ADMIN },
          },
        })

        let { data } = await this.getUserDetails()
        if (!this.isFailed) {
          this.adminDetails = new UserRecord(data[0])
          await api.patchWithSignature(`/users/${this.adminDetails.id}/role`, body)
          this.redirectToAdminsList()
          Bus.success('admin-manager-form.success-message')
        }
      } catch (e) {
        ErrorHandler.processWithoutFeedback(e)
      }

      this.hideConfirmation()
      this.isPending = false
    },

    async getUserDetails () {
      this.isFailed = false

      let response = {}
      try {
        const query = {
          filter: clearObject({
            user_id: this.provideRoleById ? this.form.id : '',
            email: !this.provideRoleById ? this.form.email : '',
            role: '',
          }),
        }

        response = await api.getWithSignature('/users', query)

        if (!response.data.length) {
          throw errors.NotFoundError
        }
      } catch (e) {
        this.isFailed = true

        switch (e) {
          case errors.NotFoundError:
            Bus.error('admin-manager-form.not-found-error-message')
            break
          default:
            Bus.error('admin-manager-form.default-error-message')
        }
      }

      return response
    },

    redirectToAdminsList () {
      this.$router.push({ name: vueRoutes.adminsList.name })
    },
  },
}
</script>

<style lang="scss" scoped>
@import '~@scss/variables';
@import '~@/vue/forms/app-form';

.admin-manager-form__form {
  display: flex;
  align-items: flex-start;
  gap: 2rem;
}

.admin-manager-form__btn {
  width: 16.5rem;
}
</style>
